import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

const NAV_LINKS = ['Features', 'How it works', 'Download'];

const FEATURES = [
  {
    title: 'Smart Timetable',
    desc: 'Automated scheduling that adapts to your campus life.',
    img: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&q=80&w=400',
    color: '#EEF2FF',
    accent: '#818CF8',
  },
  {
    title: 'Instant Alerts',
    desc: 'Push notifications for upcoming classes and examinations.',
    img: '/notifications.jpg',
    color: '#FFFBEB',
    accent: '#F59E0B',
  },
  {
    title: 'Course Manager',
    desc: 'One-tap enrollment with auto-sync technology.',
    img: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&q=80&w=2000',
    color: '#F0FDF4',
    accent: '#34D399',
  },
];

const STEPS = [
  {
    step: '01',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
    title: 'Download the App',
    desc: 'Grab the free Android APK and install it on your device in seconds.',
  },
  {
    step: '02',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    title: 'Create Your Account',
    desc: 'Register with your university email address to get verified access.',
  },
  {
    step: '03',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
    title: 'Pick Your Courses',
    desc: 'Select your enrolled units and the app builds your personal schedule.',
  },
  {
    step: '04',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </svg>
    ),
    title: 'View Your Timetable',
    desc: 'Browse lecture and exam schedules instantly when published.',
  },
];

/* ─── Scroll-aware hook ─── */
function useScrolled(threshold = 10) {
  const [scrolled, setScrolled] = useState(false);
  const prevScrolled = useRef(false);

  useEffect(() => {
    const check = () => {
      const next = window.scrollY > threshold;
      if (next !== prevScrolled.current) {
        prevScrolled.current = next;
        setScrolled(next);
      }
    };

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        check();
        rafId = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [threshold]);

  return scrolled;
}

/* ─── Fade-up reveal ─── */
function FadeUp({ children, delay = 0, style = {} }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── DownloadButton ─── */
function DownloadBtn({ style = {}, children }) {
  return (
    <motion.a
      href="/Mytimetable.apk"
      download
      style={{
        background: '#534AB7',
        color: '#fff',
        padding: '13px 26px',
        borderRadius: 12,
        fontWeight: 700,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        border: 'none',
        cursor: 'pointer',
        fontSize: 14,
        ...style,
      }}
      whileHover={{ scale: 1.04, boxShadow: '0 10px 28px rgba(83,74,183,0.38)' }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 22 }}
    >
      {children}
    </motion.a>
  );
}

/* ════════════════════════════════════════════════ */
export default function UniliaLaws() {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrolled(10);
  const navigate = useNavigate();

  return (
    <div
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: '#FFFFFF',
        color: '#0F172A',
        overflowX: 'hidden',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .mobile-toggle { display: none; }
        .footer-version-link {
          color: #94A3B8;
          text-decoration: none;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          transition: color 0.2s ease;
        }
        .footer-version-link:hover {
          color: #64748B;
        }

        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .mobile-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #F1F5F9;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 10px;
            font-size: 18px;
            cursor: pointer;
          }
          .hero-section { padding-top: 100px !important; }
          .hero-title { font-size: 38px !important; text-align: center; letter-spacing: -1px !important; }
          .hero-desc { text-align: center; margin: 0 auto 28px !important; }
          .hero-buttons { justify-content: center !important; }
          .mobile-stack { flex-direction: column !important; }
          .steps-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Navigation ──────────────────────────────────── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 1000,
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
      >
        <motion.nav
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          style={{
            padding: '16px 5%',
            background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
            backdropFilter: scrolled ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: scrolled ? 'blur(8px)' : 'none',
            borderBottom: scrolled ? '1px solid rgba(241,245,249,0.8)' : '1px solid transparent',
            transition: 'background 0.25s ease, border-color 0.25s ease, backdrop-filter 0.25s ease',
          }}
        >
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/unilia_logo.png" alt="Unilia Logo" style={{ height: 30, width: 'auto' }} />
              <span style={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '-0.03em', color: '#003366' }}>
                MyTimetable
              </span>
            </div>

            {/* Desktop links */}
            <div className="hide-mobile" style={{ display: 'flex', gap: 30 }}>
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l}
                  href={`#${l.toLowerCase().replace(/\s/g, '')}`}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.35 }}
                  whileHover={{ color: '#534AB7' }}
                  style={{
                    textDecoration: 'none',
                    color: '#64748B',
                    fontWeight: 600,
                    fontSize: 13,
                    display: 'inline-block',
                  }}
                >
                  {l}
                </motion.a>
              ))}
            </div>

            {/* Desktop CTA */}
            <motion.a
              className="hide-mobile"
              href="#"
              style={{
                background: '#534AB7',
                color: '#fff',
                padding: '9px 18px',
                borderRadius: 10,
                fontWeight: 700,
                textDecoration: 'none',
                fontSize: 13,
                display: 'inline-block',
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.38, duration: 0.35 }}
              whileHover={{ scale: 1.05, boxShadow: '0 6px 20px rgba(83,74,183,0.32)' }}
              whileTap={{ scale: 0.97 }}
            >
              Student Portal
            </motion.a>

            {/* Hamburger */}
            <button onClick={() => setMenuOpen((v) => !v)} className="mobile-toggle">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={menuOpen ? 'x' : 'h'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                >
                  {menuOpen ? '✕' : '☰'}
                </motion.span>
              </AnimatePresence>
            </button>
          </div>

          {/* Mobile dropdown */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                key="mobile-menu"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  overflow: 'hidden',
                  background: '#FFF',
                  paddingLeft: '5%',
                  paddingRight: '5%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  borderBottom: '1px solid #F1F5F9',
                }}
              >
                <div style={{ paddingTop: 16, paddingBottom: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {NAV_LINKS.map((l, i) => (
                    <motion.a
                      key={l}
                      href={`#${l.toLowerCase().replace(/\s/g, '')}`}
                      onClick={() => setMenuOpen(false)}
                      initial={{ opacity: 0, x: -14 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06, duration: 0.25 }}
                      style={{ textDecoration: 'none', color: '#0F172A', fontWeight: 700, fontSize: 15 }}
                    >
                      {l}
                    </motion.a>
                  ))}
                  <DownloadBtn style={{ justifyContent: 'center', marginTop: 4 }}>
                    Download APK
                  </DownloadBtn>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </div>

      {/* ── Hero ────────────────────────────────────────── */}
      <section
        className="hero-section"
        style={{
          paddingTop: 130,
          paddingBottom: 80,
          maxWidth: 1200,
          margin: '0 auto',
          paddingLeft: '5%',
          paddingRight: '5%',
        }}
      >
        <div className="mobile-stack" style={{ display: 'flex', alignItems: 'center', gap: 48 }}>
          {/* Text */}
          <div style={{ flex: 1.2 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#F1F0FF',
                color: '#534AB7',
                padding: '6px 14px',
                borderRadius: 100,
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 20,
              }}
            >
              <motion.span
                animate={{ scale: [1, 1.4, 1] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                style={{
                  width: 6,
                  height: 6,
                  background: '#00D26A',
                  borderRadius: '50%',
                  display: 'inline-block',
                }}
              />
              Android App Now Available
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                fontSize: 58,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-2px',
                marginBottom: 20,
              }}
            >
              Your academic life,{' '}
              <motion.span
                style={{ color: '#534AB7', display: 'inline-block' }}
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.65, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                perfectly synced.
              </motion.span>
            </motion.h1>

            <motion.p
              className="hero-desc"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.42, ease: 'easeOut' }}
              style={{
                fontSize: 16,
                color: '#64748B',
                lineHeight: 1.65,
                marginBottom: 34,
                maxWidth: 460,
              }}
            >
              The definitive scheduling platform for you. Built to ensure you never miss a lecture or exam venue again.
            </motion.p>

            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.56, ease: 'easeOut' }}
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}
            >
              <DownloadBtn>
                <span>Download APK</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </DownloadBtn>

              <motion.button
                onClick={() => navigate('/timetable')}
                style={{
                  background: 'transparent',
                  color: '#0F172A',
                  padding: '13px 26px',
                  borderRadius: 12,
                  border: '2px solid #E2E8F0',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
                whileHover={{ borderColor: '#534AB7', color: '#534AB7', scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 420, damping: 22 }}
              >
                View Master Timetable
              </motion.button>
            </motion.div>
          </div>

          {/* Banner image */}
          <motion.div
            className="hide-mobile"
            style={{ flex: 1, willChange: 'transform' }}
            initial={{ opacity: 0, x: 36 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src="/banner.jpg"
              alt="Students"
              style={{
                width: '100%',
                borderRadius: 28,
                boxShadow: '0 24px 56px rgba(83,74,183,0.1)',
                display: 'block',
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────── */}
      <section id="features" style={{ background: '#F8FAFC', padding: '80px 5%' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeUp>
            <h2
              style={{
                fontSize: 30,
                fontWeight: 800,
                marginBottom: 40,
                textAlign: 'center',
                letterSpacing: '-0.5px',
              }}
            >
              Tailored for Students
            </h2>
          </FadeUp>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -5, boxShadow: '0 16px 36px rgba(83,74,183,0.09)' }}
                style={{
                  background: '#FFF',
                  borderRadius: 22,
                  padding: 22,
                  border: '1px solid #F1F5F9',
                  cursor: 'default',
                  willChange: 'transform',
                }}
              >
                <div
                  style={{
                    height: 154,
                    background: f.color,
                    borderRadius: 14,
                    marginBottom: 18,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={f.img}
                    alt={f.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: 0.75,
                      mixBlendMode: 'multiply',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: f.accent,
                    marginBottom: 8,
                  }}
                />
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ color: '#64748B', fontSize: 14, lineHeight: 1.55 }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────── */}
      <section id="howitworks" style={{ padding: '80px 5%' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#F1F0FF',
                  color: '#534AB7',
                  padding: '6px 14px',
                  borderRadius: 100,
                  fontSize: 12,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Simple Setup
              </div>
              <h2
                style={{
                  fontSize: 34,
                  fontWeight: 800,
                  letterSpacing: '-1px',
                  marginBottom: 10,
                }}
              >
                Up and running in minutes
              </h2>
              <p
                style={{
                  color: '#64748B',
                  fontSize: 15,
                  maxWidth: 440,
                  margin: '0 auto',
                  lineHeight: 1.6,
                }}
              >
                Four simple steps to get your personalised timetable on your phone.
              </p>
            </div>
          </FadeUp>

          <div
            className="steps-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 24,
              position: 'relative',
            }}
          >
            {/* Connector line */}
            <motion.div
              className="hide-mobile"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              style={{
                position: 'absolute',
                top: 34,
                left: '12.5%',
                width: '75%',
                height: 2,
                background: 'linear-gradient(90deg, #534AB7 0%, #A5B4FC 100%)',
                transformOrigin: 'left center',
                zIndex: 0,
                borderRadius: 2,
              }}
            />

            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 18,
                  position: 'relative',
                  zIndex: 1,
                  willChange: 'transform',
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.1, boxShadow: '0 10px 26px rgba(83,74,183,0.28)' }}
                  transition={{ type: 'spring', stiffness: 380, damping: 20 }}
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: '50%',
                    background: i === 3 ? '#534AB7' : '#FFF',
                    border: i === 3 ? 'none' : '2px solid #E0E7FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: i === 3 ? '#FFF' : '#534AB7',
                    boxShadow:
                      i === 3
                        ? '0 6px 20px rgba(83,74,183,0.22)'
                        : '0 2px 10px rgba(83,74,183,0.07)',
                    position: 'relative',
                  }}
                >
                  {s.icon}
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: i === 3 ? '#00D26A' : '#534AB7',
                      color: '#FFF',
                      fontSize: 9,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #FFF',
                    }}
                  >
                    {i + 1}
                  </div>
                </motion.div>

                <div>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#A5B4FC',
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      marginBottom: 5,
                    }}
                  >
                    {s.step}
                  </p>
                  <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 7 }}>{s.title}</h3>
                  <p
                    style={{
                      color: '#64748B',
                      fontSize: 13,
                      lineHeight: 1.6,
                      maxWidth: 200,
                      margin: '0 auto',
                    }}
                  >
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <FadeUp delay={0.15}>
            <div style={{ textAlign: 'center', marginTop: 52 }}>
              <DownloadBtn>
                Get Started — Download Free
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </DownloadBtn>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── Download CTA ─────────────────────────────────── */}
      <section id="download" style={{ padding: '80px 5%' }}>
        <FadeUp>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div
              style={{
                background: '#534AB7',
                borderRadius: 28,
                padding: '56px 40px',
                color: '#FFF',
              }}
            >
              <motion.h2
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55 }}
                style={{ fontSize: 34, fontWeight: 800, marginBottom: 16 }}
              >
                The campus in your pocket.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: 0.08 }}
                style={{ opacity: 0.88, marginBottom: 28, lineHeight: 1.6, maxWidth: 480 }}
              >
                Get the MyTimetable mobile app for real-time notifications and offline access.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: 0.16 }}
                style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <motion.a
                  href="/Mytimetable.apk"
                  download
                  style={{
                    background: '#FFF',
                    color: '#534AB7',
                    padding: '11px 22px',
                    borderRadius: 10,
                    fontWeight: 800,
                    textDecoration: 'none',
                    display: 'inline-block',
                    fontSize: 14,
                  }}
                  whileHover={{ scale: 1.05, boxShadow: '0 6px 20px rgba(0,0,0,0.14)' }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                >
                  Download Android APK
                </motion.a>
                <motion.img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Play Store"
                  style={{ height: 42 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                />
              </motion.div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer
        style={{
          padding: '32px 5%',
          borderTop: '1px solid #F1F5F9',
          textAlign: 'center',
          color: '#94A3B8',
          fontSize: 12,
        }}
      >
        <p style={{ margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          © 2026 Panjie.Dev
        </p>
        <p style={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          Built with <span style={{ color: '#EF4444' }}>❤️</span> from Panjie.Dev
        </p>
        <p style={{ marginTop: 8 }}>
         <Link to="/version" className="footer-version-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 3 21 3 21 8" />
          <line x1="4" y1="20" x2="21" y2="3" />
          <polyline points="21 16 21 21 16 21" />
          <line x1="15" y1="15" x2="21" y2="21" />
        </svg>
        Version
      </Link>
        </p>
      </footer>
    </div>
  );
}